// useCreditsSelectors.ts
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../store/root-reducer';
import FeatureCodes from '../../../../../enums/feature-codes';
import { LeadRevealType } from '../../../../../../components/leads/type';

export function useCreditsSelectors() {
  const planCode = useSelector(
    (state: RootState) => state.home?.subscription?.planCode,
  );
  const planType = useSelector(
    (state: RootState) => state.home?.subscription?.planType,
  );
  const planName = useSelector(
    (state: RootState) => state.home?.subscription?.planName,
  );

  const leadFinderCredits = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit.featureCode === FeatureCodes.LeadReveal,
    ),
  );

  const leadFinderBucket1 = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit.featureCode === FeatureCodes.LeadRevealBucket1,
    ),
  );

  const leadFinderBucket2 = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit.featureCode === FeatureCodes.LeadRevealBucket2,
    ),
  );

  const leadFinderRevealTypeValue = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit?.featureCode === FeatureCodes.LeadRevealPhone,
    ),
  );

  const emailSendCredits = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit.featureCode === FeatureCodes.EmailSending,
    ),
  );

  const activeProspectCredits = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit.featureCode === FeatureCodes.ProspectMaxActive,
    ),
  );

  const prospectAddCredits = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit.featureCode === FeatureCodes.ProspectAdd,
    ),
  );

  const emailVerificationCredits = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit.featureCode === FeatureCodes.EmailVerification,
    ),
  );
  const aiCreditsCredits = useSelector((state: RootState) =>
    state.home?.credits?.find(
      (credit) => credit.featureCode === FeatureCodes.AI_CREDITS,
    ),
  );

  const dialerCredits = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'DIALER.CREDITS'
      ],
  );
  const dialerAddonCredits = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'DIALER.ADDON_CREDITS'
      ],
  );

  // Declare variables for each feature's `available` and `total` values
  // If LEAD.REVEAL exists, use it + optional BUCKET2
  // Otherwise, sum BUCKET1 + optional BUCKET2
  const leadFinderCreditsAvailable = (() => {
    if (leadFinderCredits) {
      const base = parseFloat(leadFinderCredits.accountUsageQuotaRemaining || '0');
      const bucket2Val = leadFinderBucket2
        ? parseFloat(leadFinderBucket2.accountUsageQuotaRemaining || '0')
        : 0;
      return base + bucket2Val;
    }
    if (leadFinderBucket1) {
      const bucket1Val = parseFloat(leadFinderBucket1.accountUsageQuotaRemaining || '0');
      const bucket2Val = leadFinderBucket2
        ? parseFloat(leadFinderBucket2.accountUsageQuotaRemaining || '0')
        : 0;
      return bucket1Val + bucket2Val;
    }
    return 0;
  })();
  const leadFinderRevealType =
    leadFinderRevealTypeValue?.accountUsageQuotaRemaining === '1'
      ? LeadRevealType.EMAIL_PHONE
      : LeadRevealType.EMAIL;
  const emailSendAvailable = emailSendCredits?.accountUsageQuotaRemaining;
  const emailSendTotal = emailSendCredits?.featureLimits;
  const activeProspectAvailable =
    activeProspectCredits?.accountUsageQuotaRemaining;
  const activeProspectTotal = activeProspectCredits?.featureLimits;
  const aiCreditsAvailable = aiCreditsCredits?.accountUsageQuotaRemaining;
  const aiCreditsTotal = aiCreditsCredits?.featureLimits;
  const prospectAddAvailable = prospectAddCredits?.accountUsageQuotaRemaining;
  const prospectAddTotal = prospectAddCredits?.featureLimits;
  const emailVerificationAvailable =
    emailVerificationCredits?.accountUsageQuotaRemaining;
  const dialerCreditsAvailable =
    Math.ceil(Number(dialerCredits ?? 0)) + Math.ceil(Number(dialerAddonCredits ?? 0));
  const getCoreFeaturesCreditsRequestStatus = useSelector(
    (state: RootState) => state.home.getCoreFeaturesCreditsRequest.status,
  );

  return {
    planName,
    planCode,
    planType,
    leadFinderCreditsAvailable,
    leadFinderRevealType,
    emailSendAvailable,
    emailSendTotal,
    activeProspectAvailable,
    activeProspectTotal,
    prospectAddAvailable,
    prospectAddTotal,
    emailVerificationAvailable,
    dialerCreditsAvailable,
    getCoreFeaturesCreditsRequestStatus,
    aiCreditsAvailable,
    aiCreditsTotal,
  };
}
